import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import type { ChampionList } from '@champdle/contracts';
import { normalizeSearch, searchChampions } from '@champdle/engine';
import { avatarUrl } from '../api.js';

export type ChampionItem = ChampionList['champions'][number];

interface Props {
  champions: readonly ChampionItem[];
  guessedIds: ReadonlySet<string>;
  disabled?: boolean;
  busy?: boolean;
  error?: string | null;
  onSubmit: (championId: string) => void;
}

const MAX_RESULTS = 8;

/**
 * Type-ahead guess box (ARIA 1.2 combobox). Matching is the engine's normalizeSearch, so
 * `kaisa`, `kai sa` and `Kai'Sa` are the same query. Only a champion from the list can be
 * submitted: free text never leaves this component.
 */
export function GuessInput({ champions, guessedIds, disabled, busy, error, onSubmit }: Props) {
  const uid = useId();
  const listId = `${uid}-list`;
  const optionId = (i: number): string => `${uid}-opt-${i}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const typed = normalizeSearch(query) !== '';
  const results = useMemo(
    () =>
      searchChampions(champions, query, {
        excludeIds: guessedIds,
        limit: typed ? MAX_RESULTS : champions.length,
      }),
    [champions, query, guessedIds, typed],
  );
  const expanded = open && results.length > 0;
  const noMatch = open && typed && results.length === 0;

  useEffect(() => {
    if (expanded) {
      document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
    }
    // optionId is derived from uid, which never changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, expanded]);

  const choose = (champion: ChampionItem): void => {
    onSubmit(champion.id);
    setQuery('');
    setOpen(false);
    setActive(0);
    inputRef.current?.focus();
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
    setOpen(normalizeSearch(e.target.value) !== '');
    setActive(0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        else if (results.length > 0) setActive((active + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) setOpen(true);
        else if (results.length > 0) setActive((active - 1 + results.length) % results.length);
        break;
      case 'Enter': {
        e.preventDefault();
        const picked = open ? results[active] : undefined;
        if (picked) choose(picked);
        else if (typed) setOpen(true);
        break;
      }
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
        } else if (query !== '') {
          e.preventDefault();
          setQuery('');
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  const status = !open
    ? ''
    : noMatch
      ? 'No champion matches.'
      : `${results.length} ${results.length === 1 ? 'champion' : 'champions'} available. Use up and down arrows, Enter to guess.`;

  return (
    <div className="guess">
      <label className="guess__label" htmlFor={`${uid}-input`}>
        Guess a champion
      </label>
      <div className="guess__field">
        <input
          ref={inputRef}
          id={`${uid}-input`}
          className="guess__input"
          type="text"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded ? optionId(active) : undefined}
          aria-describedby={`${uid}-hint`}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          placeholder="Type a champion name…"
          value={query}
          disabled={disabled}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(typed)}
          onBlur={() => setOpen(false)}
        />
        <ul
          id={listId}
          role="listbox"
          aria-label="Matching champions"
          className="guess__list"
          hidden={!expanded}
        >
          {results.map((c, i) => (
            <li
              key={c.id}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'guess__option is-active' : 'guess__option'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(c)}
              onMouseMove={() => i !== active && setActive(i)}
            >
              <img src={avatarUrl(c.avatar)} alt="" width={40} height={40} loading="lazy" />
              <span>{c.name}</span>
            </li>
          ))}
        </ul>
      </div>
      <p id={`${uid}-hint`} className="guess__hint">
        {busy ? 'Checking…' : noMatch ? 'No champion matches.' : (error ?? ' ')}
      </p>
      <div role="status" aria-live="polite" className="sr-only">
        {status}
      </div>
      {error ? (
        <div role="alert" className="sr-only">
          {error}
        </div>
      ) : null}
    </div>
  );
}
